import { useEffect } from 'react';
import Layout from '../../../components/feature/Layout';
import SchemaOrg from '../../../components/base/SchemaOrg';

export default function RestaurantPipePage() {
  useEffect(() => {
    const pageTitle = '餐廳水管疏通｜商業廚房排水管清潔｜永業環保';
    const pageDesc = '永業環保專業餐廳水管疏通服務，解決油脂堵塞、排水不良問題。提供定期維護方案，預防管線堵塞，確保廚房正常運作。20年經驗，快速到府，不影響營業。';
    const pageKeywords = '餐廳水管疏通,商業廚房排水,油脂管清理,餐廳管線維護,廚房水管堵塞';
    const pageUrl = `${siteUrl}/seo/restaurant-pipe`;

    document.title = pageTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', pageDesc);
    setMeta('meta[name="keywords"]', 'content', pageKeywords);
    setMeta('link[rel="canonical"]', 'href', pageUrl);
    setMeta('meta[property="og:title"]', 'content', pageTitle);
    setMeta('meta[property="og:description"]', 'content', pageDesc);
    setMeta('meta[property="og:url"]', 'content', pageUrl);
    setMeta('meta[name="twitter:title"]', 'content', pageTitle);
    setMeta('meta[name="twitter:description"]', 'content', pageDesc);
  }, [siteUrl]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const restaurantProblems = [
    {
      title: '油脂堆積堵塞',
      description: '長期烹飪產生的油脂附著管壁，逐漸縮小管徑造成堵塞',
      icon: 'ri-drop-line',
      severity: 'high',
      image: 'https://public.readdy.ai/ai/img_res/d33698a9fceb8e4dbd8800eaf3e0167d.jpg'
    },
    {
      title: '食物殘渣累積',
      description: '菜渣、米粒等食物殘渣卡在管線中，混合油脂形成頑固堵塞',
      icon: 'ri-restaurant-line',
      severity: 'high',
      image: 'https://readdy.ai/api/search-image?query=Food%20waste%20and%20debris%20accumulated%20in%20restaurant%20drain%20pipe%2C%20mixture%20of%20vegetable%20scraps%20and%20grease%20forming%20blockage%2C%20close-up%20technical%20photography%2C%20professional%20kitchen%20maintenance%20documentation%2C%20bright%20lighting%20showing%20texture%20details&width=600&height=400&seq=restaurant-problem-2&orientation=landscape'
    },
    {
      title: '排水速度變慢',
      description: '水槽排水緩慢，洗碗槽積水，影響廚房作業效率',
      icon: 'ri-time-line',
      severity: 'medium',
      image: 'https://readdy.ai/api/search-image?query=Commercial%20kitchen%20sink%20with%20slow%20draining%20water%20pooling%2C%20stainless%20steel%20sink%20in%20busy%20restaurant%20kitchen%2C%20water%20level%20rising%2C%20professional%20kitchen%20environment%2C%20overhead%20lighting%2C%20realistic%20photography&width=600&height=400&seq=restaurant-problem-3&orientation=landscape'
    },
    {
      title: '異味產生',
      description: '管線內腐敗物質產生惡臭，影響廚房環境與食品衛生',
      icon: 'ri-nose-line',
      severity: 'medium',
      image: 'https://readdy.ai/api/search-image?query=Restaurant%20kitchen%20drain%20emitting%20unpleasant%20odor%2C%20visual%20representation%20of%20bad%20smell%20with%20wavy%20lines%2C%20stainless%20steel%20drain%20cover%2C%20clean%20commercial%20kitchen%20background%2C%20professional%20hygiene%20concern%20illustration&width=600&height=400&seq=restaurant-problem-4&orientation=landscape'
    }
  ];

  const solutions = [
    {
      title: '高壓水刀疏通',
      description: '使用專業高壓水刀設備，強力沖刷管壁油脂，徹底清除堵塞物',
      features: ['壓力可達3000PSI', '不傷管線', '清潔徹底', '效果持久'],
      icon: 'ri-contrast-drop-2-line'
    },
    {
      title: '環保除油劑',
      description: '使用食品級環保除油劑，分解頑固油脂，安全無毒不傷管線',
      features: ['食品級安全', '強效除油', '環保配方', '無腐蝕性'],
      icon: 'ri-flask-line'
    },
    {
      title: '內視鏡檢查',
      description: '使用管線內視鏡深入檢查，精準定位問題點，對症下藥',
      features: ['精準定位', '全程記錄', '問題可視化', '預防性檢查'],
      icon: 'ri-search-eye-line'
    },
    {
      title: '定期維護方案',
      description: '提供月度或季度定期維護服務，預防堵塞，確保管線暢通',
      features: ['預防堵塞', '成本節省', '優先服務', '專屬優惠'],
      icon: 'ri-calendar-check-line'
    }
  ];

  const maintenancePlans = [
    {
      name: '基礎方案',
      frequency: '每季一次',
      suitable: '小型餐廳、咖啡廳',
      services: [
        '主要排水管疏通',
        '水槽排水檢查',
        '基礎除油處理',
        '排水狀況報告'
      ],
      price: '$3,500/次',
      discount: '年約9折'
    },
    {
      name: '標準方案',
      frequency: '每月一次',
      suitable: '中型餐廳、快餐店',
      services: [
        '全管線高壓疏通',
        '內視鏡檢查',
        '環保除油處理',
        '油脂截留器清理',
        '詳細檢測報告',
        '24小時緊急服務'
      ],
      price: '$5,800/次',
      discount: '年約85折',
      popular: true
    },
    {
      name: '專業方案',
      frequency: '每月兩次',
      suitable: '大型餐廳、飯店廚房',
      services: [
        '全系統深度疏通',
        '定期內視鏡檢查',
        '專業除油維護',
        '油脂截留器清理',
        '化糞池檢查',
        '完整維護記錄',
        '24小時優先服務',
        '專屬技術顧問'
      ],
      price: '$9,800/次',
      discount: '年約8折'
    }
  ];

  const restaurantCases = [
    {
      name: '鼎泰豐中壢店',
      type: '知名連鎖餐廳',
      problem: '廚房主排水管嚴重油脂堵塞，影響營業',
      solution: '使用高壓水刀配合專業除油劑，2小時內完成疏通',
      result: '排水恢復正常，簽訂月度維護合約',
      image: 'https://readdy.ai/api/search-image?query=Famous%20chain%20restaurant%20kitchen%20with%20professional%20plumber%20working%20on%20main%20drainage%20system%2C%20modern%20commercial%20kitchen%20interior%2C%20stainless%20steel%20equipment%2C%20worker%20in%20uniform%20using%20high-pressure%20water%20jet%2C%20bright%20professional%20lighting%2C%20service%20documentation%20photography&width=800&height=600&seq=restaurant-case-1&orientation=landscape'
    },
    {
      name: '王品牛排桃園店',
      type: '高級牛排館',
      problem: '多處水槽排水緩慢，油脂截留器滿溢',
      solution: '全管線疏通+油脂截留器清理+內視鏡檢查',
      result: '建立定期維護機制，預防問題發生',
      image: 'https://readdy.ai/api/search-image?query=Upscale%20steakhouse%20kitchen%20maintenance%20service%2C%20professional%20technician%20cleaning%20grease%20trap%20and%20drain%20system%2C%20luxury%20restaurant%20kitchen%20environment%2C%20modern%20equipment%2C%20detailed%20service%20work%2C%20professional%20documentation%20style&width=800&height=600&seq=restaurant-case-2&orientation=landscape'
    },
    {
      name: '春水堂台北店',
      type: '茶飲餐廳',
      problem: '飲料吧台排水管堵塞，茶渣累積',
      solution: '專業疏通設備清理茶渣，安裝過濾網',
      result: '排水順暢，提供預防性維護建議',
      image: 'https://readdy.ai/api/search-image?query=Bubble%20tea%20restaurant%20bar%20counter%20drain%20cleaning%20service%2C%20beverage%20station%20with%20professional%20plumber%2C%20tea%20residue%20removal%2C%20modern%20drink%20preparation%20area%2C%20clean%20commercial%20space%2C%20service%20documentation%20photography&width=800&height=600&seq=restaurant-case-3&orientation=landscape'
    }
  ];

  const preventionTips = [
    {
      title: '安裝油脂截留器',
      description: '在排水管前端安裝油脂截留器，攔截大部分油脂，減少管線堵塞',
      icon: 'ri-filter-line'
    },
    {
      title: '定期清理截留器',
      description: '每週清理油脂截留器，避免油脂溢出進入管線',
      icon: 'ri-delete-bin-line'
    },
    {
      title: '熱水沖洗管線',
      description: '每日營業結束後用熱水沖洗排水管，溶解附著的油脂',
      icon: 'ri-fire-line'
    },
    {
      title: '避免倒入油脂',
      description: '廢油應集中回收，避免直接倒入水槽排水管',
      icon: 'ri-prohibit-line'
    },
    {
      title: '使用濾網',
      description: '水槽安裝濾網，攔截食物殘渣，防止進入管線',
      icon: 'ri-grid-line'
    },
    {
      title: '定期專業維護',
      description: '每月安排專業疏通保養，預防勝於治療',
      icon: 'ri-tools-line'
    }
  ];

  const faqItems = [
    {
      question: '餐廳水管多久需要疏通一次？',
      answer: '建議每月至少疏通一次。高油脂餐廳（如火鍋店、燒烤店）建議每2週一次。定期維護可以預防嚴重堵塞，避免影響營業。我們提供月度和季度維護方案，比臨時叫修更經濟實惠。'
    },
    {
      question: '疏通會影響餐廳營業嗎？',
      answer: '我們可以配合餐廳營業時間，安排在非營業時段（如早上或深夜）進行。一般疏通作業約需1-2小時，不會影響正常營業。緊急狀況我們也提供24小時服務。'
    },
    {
      question: '餐廳水管疏通費用如何計算？',
      answer: '費用依管線長度、堵塞程度、作業難度而定。一般餐廳疏通約$3,000-$8,000。我們提供免費現場評估和報價，也有優惠的定期維護方案，年約可享85折優惠。'
    },
    {
      question: '你們使用的藥劑安全嗎？',
      answer: '我們使用食品級環保除油劑，對人體無害，不會污染食材。所有藥劑都符合食品安全標準，並有合格證明。主要使用高壓水刀物理疏通，藥劑僅作為輔助。'
    },
    {
      question: '如何預防餐廳水管堵塞？',
      answer: '建議：1.安裝油脂截留器並定期清理 2.避免將廢油倒入水槽 3.使用濾網攔截食物殘渣 4.每日用熱水沖洗管線 5.定期安排專業疏通保養。我們也提供預防性維護方案。'
    },
    {
      question: '油脂截留器需要清理嗎？',
      answer: '是的，油脂截留器需要定期清理，建議每週至少一次。如果截留器滿溢，油脂會直接進入管線造成堵塞。我們的維護方案包含油脂截留器清理服務。'
    }
  ];

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${siteUrl}/seo/restaurant-pipe#service`,
      name: '餐廳水管疏通服務',
      description: '專業餐廳水管疏通服務，解決油脂堵塞、排水不良問題。提供定期維護方案，預防管線堵塞，確保廚房正常運作。',
      provider: {
        '@type': 'LocalBusiness',
        name: '永業環保',
        telephone: '0800-281-281',
        url: siteUrl
      },
      serviceType: '商業廚房排水管清潔',
      areaServed: [
        { '@type': 'City', name: '桃園市' },
        { '@type': 'City', name: '新北市' },
        { '@type': 'City', name: '台北市' },
        { '@type': 'City', name: '新竹市' },
        { '@type': 'City', name: '苗栗縣' }
      ],
      offers: {
        '@type': 'Offer',
        priceRange: 'NT$3,000-NT$8,000',
        priceCurrency: 'TWD'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${siteUrl}/seo/restaurant-pipe#faqpage`,
      mainEntity: faqItems.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${siteUrl}/seo/restaurant-pipe#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首頁', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '餐廳水管疏通', item: `${siteUrl}/seo/restaurant-pipe` }
      ]
    }
  ];

  return (
    <Layout>
      <SchemaOrg schema={schemas} />
      <div className="pt-24">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-orange-50 via-white to-red-50 py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                餐廳水管疏通專業服務
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                專業解決餐廳油脂堵塞、排水不良問題<br/>
                提供定期維護方案，預防管線堵塞，確保廚房正常運作
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="/contact" 
                  className="inline-flex items-center gap-2 bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-xl"></i>
                  立即預約服務
                </a>
                <a 
                  href="#maintenance-plans" 
                  className="inline-flex items-center gap-2 bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold border-2 border-orange-600 hover:bg-orange-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-file-list-line text-xl"></i>
                  查看維護方案
                </a>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-orange-600 mb-2">500+</div>
                <div className="text-gray-600">餐廳客戶</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-orange-600 mb-2">24hr</div>
                <div className="text-gray-600">緊急服務</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-orange-600 mb-2">2hr</div>
                <div className="text-gray-600">快速到府</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-orange-600 mb-2">食品級</div>
                <div className="text-gray-600">安全藥劑</div>
              </div>
            </div>
          </div>
        </section>

        {/* Restaurant Problems */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">餐廳常見水管問題</h2>
              <p className="text-lg text-gray-600">了解問題根源，才能有效解決</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {restaurantProblems.map((problem, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={problem.image}
                      alt={problem.title}
                      className="w-full h-full object-cover"
                    />
                    {problem.severity === 'high' && (
                      <div className="absolute top-4 right-4">
                        <span className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                          嚴重影響
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 ${
                        problem.severity === 'high' ? 'bg-red-100' : 'bg-orange-100'
                      } rounded-lg flex items-center justify-center`}>
                        <i className={`${problem.icon} text-2xl ${
                          problem.severity === 'high' ? 'text-red-600' : 'text-orange-600'
                        }`}></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{problem.title}</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">{problem.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Solutions */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">專業解決方案</h2>
              <p className="text-lg text-gray-600">針對餐廳特性，提供最有效的疏通方案</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {solutions.map((solution, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <i className={`${solution.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{solution.title}</h3>
                  <p className="text-gray-600 text-center mb-6 leading-relaxed">{solution.description}</p>
                  <div className="space-y-2">
                    {solution.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-checkbox-circle-fill text-orange-600"></i>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Maintenance Plans */}
        <section id="maintenance-plans" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">定期維護方案</h2>
              <p className="text-lg text-gray-600">預防勝於治療，定期維護更省錢</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {maintenancePlans.map((plan, index) => (
                <div 
                  key={index}
                  className={`relative bg-white rounded-2xl p-8 border-2 ${
                    plan.popular ? 'border-orange-600 shadow-2xl' : 'border-gray-200 shadow-lg'
                  } hover:shadow-2xl transition-all hover:-translate-y-2`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                        最受歡迎
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="text-orange-600 font-semibold mb-2">{plan.frequency}</div>
                    <div className="text-sm text-gray-500">{plan.suitable}</div>
                  </div>
                  <div className="mb-6">
                    <div className="text-3xl font-bold text-gray-900 text-center mb-2">{plan.price}</div>
                    <div className="text-center">
                      <span className="inline-block bg-orange-100 text-orange-600 px-4 py-1 rounded-full text-sm font-semibold">
                        {plan.discount}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3 mb-8">
                    {plan.services.map((service, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                        <span className="text-gray-600 text-sm">{service}</span>
                      </div>
                    ))}
                  </div>
                  <a 
                    href="/contact" 
                    className={`block text-center py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                      plan.popular 
                        ? 'bg-orange-600 text-white hover:bg-orange-700' 
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    立即訂購
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Restaurant Cases */}
        <section className="py-20 bg-gradient-to-br from-orange-50 to-red-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">餐廳服務案例</h2>
              <p className="text-lg text-gray-600">服務知名連鎖餐廳，品質有保證</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {restaurantCases.map((caseItem, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={caseItem.image}
                      alt={caseItem.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{caseItem.name}</h3>
                    <div className="text-sm text-orange-600 font-semibold mb-4">{caseItem.type}</div>
                    <div className="space-y-3 text-sm">
                      <div>
                        <strong className="text-gray-900">問題：</strong>
                        <p className="text-gray-600 mt-1">{caseItem.problem}</p>
                      </div>
                      <div>
                        <strong className="text-gray-900">處理：</strong>
                        <p className="text-gray-600 mt-1">{caseItem.solution}</p>
                      </div>
                      <div>
                        <strong className="text-gray-900">結果：</strong>
                        <p className="text-gray-600 mt-1">{caseItem.result}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Prevention Tips */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">預防堵塞小技巧</h2>
              <p className="text-lg text-gray-600">日常保養，減少堵塞發生</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {preventionTips.map((tip, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${tip.icon} text-2xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{tip.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{tip.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">常見問題</h2>
              <p className="text-lg text-gray-600">關於餐廳水管疏通的常見疑問</p>
            </div>

            <div className="space-y-6">
              {faqItems.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-start gap-3">
                    <i className="ri-question-line text-orange-600 text-2xl"></i>
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 leading-relaxed pl-11">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-orange-600 to-red-600">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              餐廳水管問題？立即聯絡我們
            </h2>
            <p className="text-xl text-orange-50 mb-8">
              24小時緊急服務，2小時內快速到府<br/>
              專業團隊為您解決所有餐廳水管問題
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="/contact" 
                className="inline-flex items-center gap-2 bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                立即預約服務
              </a>
              <a 
                href="/services/pipe-cleaning" 
                className="inline-flex items-center gap-2 bg-orange-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-information-line text-xl"></i>
                了解更多服務
              </a>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}